import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from 'react'
import { Editor as TipTapEditor } from '@tiptap/react'
import { Resizable } from 're-resizable'
import debounce from 'lodash.debounce'
import max from 'lodash.max'

import { cn } from '@/utils'
import { Editor } from '@/components/editor/Editor'
import { Spinner } from '@/components/ui/spinner'
import { useEditDocPageNote } from '@/services/queries/document_notes/useEditDocPageNote'
import { useDocumentState } from '@/state/document'
import {
  Note,
  TemplateAttribute,
  useListNotes,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'

import { useAnnotationLayer } from '../Annotations/queries'
import {
  getAbsoluteXFromDocumentPageOrder,
  getAbsoluteYFromSubPage,
} from '../Annotations/utils'
import { DocumentPage } from '@/lib/api/client'
import { useAnnotationsContext } from '../Annotations/AnnotationsContext'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useParams } from 'wouter'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import { useIsTemplateAttributeMultiSelected } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useIsTemplateAttributeMultiSelected'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'
import { useMultiSelectHandler } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectHandler'
import { useMultiSelectKeyPressed } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectKeyPressed'

interface OperationNotesEditorProps {
  template: TemplateAttribute<Note>
  isReadOnly: boolean
  documentPage: DocumentPage
  dataType: 'note'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}

export const OperationNotesEditor = ({
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: OperationNotesEditorProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])
  const editorRef = useRef<TipTapEditor | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )

  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
  } = useAnnotationLayer(documentPageId)

  const notesTemplates = useListNotes(documentPageId)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const croppingImageId = useDocumentState((state) => state.croppingImageId)
  const { mutateAsync: updateTemplate, isPending: isUpdatingTemplate } =
    useUpdateTemplateElement({
      documentPageId,
    })
  const selectElement = useDocumentState((state) => state.setSelectedElement)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      selectedElement?.id === template?.id,
    [selectedElement, documentPageId, template?.id],
  )
  const isMultiSelected = useIsTemplateAttributeMultiSelected({
    templateAttributeId: template.id,
  })
  const positionData = useMemo(
    () => template?.template_values?.position,
    [template],
  )
  const subPageId = useMemo(
    () => template?.template_values?.subPageId || documentPageId,
    [template],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )
  const size = useMemo(() => template?.template_values?.size, [template])

  const notesPosition = useMemo(
    () => template?.template_values?.notesPosition,
    [template],
  )
  const documentPageOrder = useMemo(
    () => notesTemplates?.findIndex((note) => note.id === template?.id),
    [notesTemplates, template],
  )
  const isAbsolute = useMemo(
    () => notesPosition === 'absolute',
    [notesPosition],
  )

  const updateNotesSizeHandler = useCallback(
    async (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      await updateTemplate({
        payload: {
          size,
          position: { ...position, z: positionData?.z },
        },
        data_type: dataType,
        templateAttributeId: template?.id as string,
      })
    },
    [dataType, template?.id, positionData, updateTemplate],
  )

  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  })
  const containerDimensionsRef = useRef(containerDimensions)
  const resizeStartSizeRef = useRef<{ width: number; height: number } | null>(
    null,
  )
  const isResizingRef = useRef(false)

  useEffect(() => {
    let resizeObserver: ResizeObserver
    const minDimensions = { width: 300, height: 100 }
    if (containerRef.current && !size) {
      const rect = containerRef.current.getBoundingClientRect()
      const width = max([rect.width, minDimensions.width])
      const height = max([rect.height, minDimensions.height])
      setContainerDimensions({
        width,
        height,
      })

      resizeObserver = new ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect

        setContainerDimensions({
          width: max([width, minDimensions.width]),
          height: max([height, minDimensions.height]),
        })
      })

      resizeObserver.observe(containerRef.current)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [containerRef, size])

  useEffect(() => {
    if (size) {
      setContainerDimensions(size)
    }
  }, [size])

  useLayoutEffect(() => {
    containerDimensionsRef.current = containerDimensions
  }, [containerDimensions])

  const { setIsResizing } = useAnnotationsContext()
  const [{ position, delta }, setPosition] = useState({
    position: positionData,
    delta: INITIAL_DELTA,
  })
  const { mutate: updateNotes } = useEditDocPageNote({
    documentPageId,
    notesId: template?.id as string,
    useOptimisticUpdate: true,
  })
  const containerId = useMemo(
    () => `${documentPageId}-${template?.id}`,
    [documentPageId, template?.id],
  )

  const { attributes, isDragging, listeners, transform, setNodeRef } =
    useMultiSelectDraggable({
      disabled: isAddingElement || isReadOnly || !!croppingImageId,
      id: template?.id as string,
      data: { type: dataType },
    })

  const { bind, previewDelta } = useKeyboardNudge({
    isActive: isSelected && Boolean(selectedElement?.isRemovable),
    showGridOnNudge: true,
    onNudgeStart,
    onNudgeEnd,
    template,
    documentPage,
  })

  useEffect(() => {
    const tX = transform?.x ?? 0
    const tY = transform?.y ?? 0
    const pX = previewDelta?.x ?? 0
    const pY = previewDelta?.y ?? 0

    // If everything is 0, this is typically the "drag ended" / "idle" state.
    // Don't overwrite delta here — let it be reset only when positionData changes.
    if (tX === 0 && tY === 0 && pX === 0 && pY === 0) {
      return
    }

    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (tX + pX) / zoomRatio,
        y: (tY + pY) / zoomRatio,
      },
    }))
  }, [transform, previewDelta, zoomRatio])

  useEffect(() => {
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  const saveNotes = useCallback(
    debounce(({ editor }: { editor: TipTapEditor }) => {
      if (isReadOnly) return
      updateNotes({
        editor,
        notesPosition: notesPosition as 'absolute' | 'relative',
      })
    }, 500),
    [updateNotes, isReadOnly],
  )

  const getEditorElement = useCallback((editor: TipTapEditor) => {
    let element: HTMLElement | null = null
    const editorElement = editor.options.element

    if (editorElement instanceof HTMLElement) {
      element = editorElement
    } else if (
      editorElement &&
      typeof editorElement === 'object' &&
      'mount' in editorElement
    ) {
      element = editorElement.mount as HTMLElement
    }

    return element
  }, [])

  const measureEditorHeightForWidth = useCallback(
    (editor: TipTapEditor, width: number) => {
      const element = getEditorElement(editor)

      if (!element) {
        return 0
      }

      const computedStyles = window.getComputedStyle(element)
      const measurer = document.createElement('div')

      measurer.style.position = 'absolute'
      measurer.style.visibility = 'hidden'
      measurer.style.pointerEvents = 'none'
      measurer.style.left = '-99999px'
      measurer.style.top = '0'
      measurer.style.boxSizing = computedStyles.boxSizing
      measurer.style.width = `${width}px`
      measurer.style.minWidth = `${width}px`
      measurer.style.maxWidth = `${width}px`
      measurer.style.padding = computedStyles.padding
      measurer.style.border = computedStyles.border
      measurer.style.font = computedStyles.font
      measurer.style.fontFamily = computedStyles.fontFamily
      measurer.style.fontSize = computedStyles.fontSize
      measurer.style.fontWeight = computedStyles.fontWeight
      measurer.style.lineHeight = computedStyles.lineHeight
      measurer.style.letterSpacing = computedStyles.letterSpacing
      measurer.style.whiteSpace = 'pre-wrap'
      measurer.style.wordBreak = computedStyles.wordBreak
      measurer.style.overflowWrap = computedStyles.overflowWrap
      measurer.style.overflow = 'visible'
      measurer.innerHTML = element.innerHTML

      document.body.appendChild(measurer)
      const measuredHeight = Math.ceil(measurer.scrollHeight)
      document.body.removeChild(measurer)

      return measuredHeight
    },
    [getEditorElement],
  )

  const findRequiredWidthForHeight = useCallback(
    ({
      editor,
      targetHeight,
      currentWidth,
      maxWidth,
    }: {
      editor: TipTapEditor
      targetHeight: number
      currentWidth: number
      maxWidth: number
    }) => {
      let low = Math.max(currentWidth, 150)
      let high = maxWidth
      let bestWidth = high

      while (low <= high) {
        const middleWidth = Math.floor((low + high) / 2)
        const measuredHeight = measureEditorHeightForWidth(editor, middleWidth)

        if (measuredHeight <= targetHeight) {
          bestWidth = middleWidth
          high = middleWidth - 1
        } else {
          low = middleWidth + 1
        }
      }

      return bestWidth
    },
    [measureEditorHeightForWidth],
  )

  const adjustEditorHeight = useCallback(
    ({
      editor,
      size,
    }: {
      editor: TipTapEditor
      size: { height: number; width: number }
    }) => {
      let element: HTMLElement | null = null
      const editorElement = editor.options.element

      if (editorElement instanceof HTMLElement) {
        element = editorElement
      } else if (
        editorElement &&
        typeof editorElement === 'object' &&
        'mount' in editorElement
      ) {
        element = editorElement.mount as HTMLElement
      }

      const scrollHeight = element?.scrollHeight || 0
      const containerHeight = size.height
      const height = Math.max(scrollHeight, containerHeight)
      const updatedSize = {
        height,
        width: size.width,
      }

      const isEqual =
        updatedSize.height === size.height && updatedSize.width === size.width

      if (!isEqual) {
        setContainerDimensions(updatedSize)
      }
      return updatedSize
    },
    [],
  )

  useEffect(() => {
    // Skip this effect during active resize to prevent interference
    if (isResizingRef.current) {
      return
    }

    const editor = editorRef.current
    if (!editor) {
      return
    }

    const currentSize = size || containerDimensions

    if (!currentSize.width || !currentSize.height) {
      return
    }

    let updatedSize = currentSize
    if (editor) {
      updatedSize = adjustEditorHeight({
        editor,
        size: currentSize,
      })
    }

    const isEqual =
      updatedSize.height === currentSize.height &&
      updatedSize.width === currentSize.width

    if (isEqual) {
      return
    }

    adjustEditorHeight({
      editor,
      size: updatedSize,
    })
    saveNotes({ editor })
  }, [adjustEditorHeight, saveNotes, size, containerDimensions])

  const mutatedListeners = useMemo(
    () => ({
      ...listeners,
      onMouseDown: (e: React.MouseEvent<Element, MouseEvent>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
      onTouchStart: (e: React.TouchEvent<Element>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
    }),
    [listeners, containerId],
  )

  const isLoading = useMemo(() => isUpdatingTemplate, [isUpdatingTemplate])

  const rawContent = template?.template_values?.content ?? ''

  const content = useMemo(() => rawContent, [rawContent])

  const { setDraggableAnnotationParentRef } = useRelativeToAbsolutePositioning()
  const {
    onPointerDown: multiSelectKeydownHandler,
    onPointerUp: multiSelectKeyupHandler,
  } = useMultiSelectHandler()
  const isSelectKeyPressed = useMultiSelectKeyPressed()
  const [isEditorFocused, setIsEditorFocused] = useState(false)

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      onAuxClick={() => {
        selectElement({
          documentPageId,
          id: template?.id as string,
          isRemovable: true,
          type: dataType,
        })
      }}
      optionsGroups={[
        {
          options: [
            {
              label: 'Bring to front',
              action: () => bringToFront(template?.id as string),
            },
            {
              label: 'Bring forward',
              action: () => bringForward(template?.id as string),
            },
            {
              label: 'Send backward',
              action: () => sendBackward(template?.id as string),
            },
            {
              label: 'Send to back',
              action: () => sendToBack(template?.id as string),
            },
          ],
        },
      ]}
    >
      <div
        className="flex flex-1 p-px absolute top-0 left-0"
        ref={(el) => {
          if (template?.id) {
            setDraggableAnnotationParentRef(template.id, el)
          }
        }}
      >
        <div
          className="relative flex flex-1 h-full w-full"
          ref={containerRef}
        />
        <div
          id={containerId}
          ref={setNodeRef}
          className={cn('border-4 flex rounded-md box-content absolute', {
            'cursor-move': !isReadOnly,
            'cursor-default': isReadOnly,
            'rounded-md': isSelected,
            'shadow-md': isDragging,
            'shadow-gray-200': isDragging,
            'bg-sky-200/10': isDragging,
            'z-[100]': isDragging,
          })}
          {...bind}
          {...attributes}
          {...mutatedListeners}
          style={{
            translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
            top: getAbsoluteYFromSubPage({
              subPageId,
              relativeY: position?.y || 0,
              subPageIds: documentSubPages,
              isRelative: !isAbsolute,
              relativeOffset: 350,
            }),
            left: getAbsoluteXFromDocumentPageOrder({
              relativeX: position?.x || 0,
              isRelative: !isAbsolute,
              documentPageOrder,
              relativeOffset: 350,
            }),
            zIndex: position?.z || 1000,
            borderColor:
              isSelected || isMultiSelected
                ? 'rgba(129, 140, 248, 0.5)'
                : 'transparent',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Resizable
            minWidth={150}
            minHeight={45}
            maxWidth={740}
            size={
              containerDimensions.width > 0 && containerDimensions.height > 0
                ? containerDimensions
                : undefined
            }
            onResizeStart={(event, _, ref) => {
              event.stopPropagation()
              const width = ref.clientWidth
              const height = ref.clientHeight
              const nextSize = { width, height }
              if (setIsResizing) setIsResizing(true)
              isResizingRef.current = true
              resizeStartSizeRef.current = nextSize
              containerDimensionsRef.current = nextSize
              setContainerDimensions(nextSize)
            }}
            onResize={(event, direction, ref) => {
              event.stopPropagation()

              const previousSize = containerDimensionsRef.current
              const manualWidth = ref.clientWidth
              const manualHeight = ref.clientHeight
              let nextWidth = manualWidth
              let nextHeight = manualHeight
              const manualWidthDiff = manualWidth - previousSize.width
              const manualHeightDiff = manualHeight - previousSize.height
              const isWidthShrinking = manualWidthDiff < 0
              const isHeightShrinking = manualHeightDiff < 0

              if (editorRef.current) {
                if (isWidthShrinking) {
                  const persistedHeightFloor = Math.max(size?.height || 0, 45)
                  const requiredHeight = measureEditorHeightForWidth(
                    editorRef.current,
                    manualWidth,
                  )

                  if (requiredHeight > persistedHeightFloor) {
                    nextHeight = Math.max(manualHeight, requiredHeight)
                    ref.style.height = `${nextHeight}px`
                  }
                }

                if (isHeightShrinking) {
                  const persistedWidthFloor = Math.max(size?.width || 0, 150)
                  const requiredWidth = findRequiredWidthForHeight({
                    editor: editorRef.current,
                    targetHeight: manualHeight,
                    currentWidth: manualWidth,
                    maxWidth: 740,
                  })

                  if (requiredWidth > persistedWidthFloor) {
                    nextWidth = Math.max(manualWidth, requiredWidth)
                    ref.style.width = `${nextWidth}px`
                  }

                  const requiredHeightAtNextWidth = measureEditorHeightForWidth(
                    editorRef.current,
                    nextWidth,
                  )

                  if (requiredHeightAtNextWidth > manualHeight) {
                    nextHeight = Math.max(nextHeight, requiredHeightAtNextWidth)
                    ref.style.height = `${nextHeight}px`
                  }
                }
              }

              const nextSize = {
                width: nextWidth,
                height: nextHeight,
              }

              // Calculate actual change in size (accounts for min/max constraints)
              const actualWidthDiff = nextWidth - previousSize.width
              const actualHeightDiff = nextHeight - previousSize.height

              const deltaUpdates = {
                topLeft: { x: -actualWidthDiff, y: -actualHeightDiff },
                bottomLeft: { x: -actualWidthDiff, y: 0 },
                topRight: { x: 0, y: -actualHeightDiff },
                bottomRight: { x: 0, y: 0 },
                top: { x: 0, y: -actualHeightDiff },
                right: { x: 0, y: 0 },
                bottom: { x: 0, y: 0 },
                left: { x: -actualWidthDiff, y: 0 },
              }

              containerDimensionsRef.current = nextSize
              setContainerDimensions(nextSize)

              setPosition((prevPosition) => ({
                ...prevPosition,
                delta: {
                  x: prevPosition.delta.x + (deltaUpdates[direction]?.x || 0),
                  y: prevPosition.delta.y + (deltaUpdates[direction]?.y || 0),
                },
              }))
            }}
            onResizeStop={(event, _direction, ref) => {
              event.stopPropagation()
              if (setIsResizing) setIsResizing(false)
              isResizingRef.current = false
              if (!editorRef.current) return

              const manualWidth = ref.clientWidth
              const manualHeight = ref.clientHeight
              let finalWidth = manualWidth
              let finalHeight = manualHeight
              const persistedHeightFloor = Math.max(size?.height || 0, 45)
              const persistedWidthFloor = Math.max(size?.width || 0, 150)

              const requiredHeightAtManualWidth = measureEditorHeightForWidth(
                editorRef.current,
                manualWidth,
              )

              if (requiredHeightAtManualWidth > persistedHeightFloor) {
                finalHeight = Math.max(finalHeight, requiredHeightAtManualWidth)
              }

              const requiredWidthAtManualHeight = findRequiredWidthForHeight({
                editor: editorRef.current,
                targetHeight: manualHeight,
                currentWidth: manualWidth,
                maxWidth: 740,
              })

              if (requiredWidthAtManualHeight > persistedWidthFloor) {
                finalWidth = Math.max(finalWidth, requiredWidthAtManualHeight)
              }

              const requiredHeightAtFinalWidth = measureEditorHeightForWidth(
                editorRef.current,
                finalWidth,
              )

              if (requiredHeightAtFinalWidth > manualHeight) {
                finalHeight = Math.max(finalHeight, requiredHeightAtFinalWidth)
              }

              const latestSize = {
                width: finalWidth,
                height: finalHeight,
              }

              if (latestSize.width < 150 || latestSize.height < 45) return

              let updatedSize = latestSize
              if (editorRef.current) {
                updatedSize = adjustEditorHeight({
                  editor: editorRef.current,
                  size: latestSize,
                })
              }

              containerDimensionsRef.current = updatedSize
              setContainerDimensions(updatedSize)
              resizeStartSizeRef.current = null

              updateNotesSizeHandler(
                {
                  width: updatedSize.width,
                  height: updatedSize.height,
                },
                {
                  x: (position?.x || 0) + delta.x,
                  y: (position?.y || 0) + delta.y,
                },
              )
                .then(() => {
                  selectElement({
                    documentPageId,
                    id: template?.id as string,
                    isRemovable: true,
                    type: dataType,
                  })
                })
                .catch((error) => {
                  console.error('Error updating image size:', error)
                })
            }}
            handleClasses={{
              topRight: !isReadOnly && isSelected ? 'resize-handle' : '',
              topLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
              bottomRight: !isReadOnly && isSelected ? 'resize-handle' : '',
              bottomLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
              top:
                !isReadOnly && isSelected
                  ? 'resize-handle resize-handle--top'
                  : '',
              right:
                !isReadOnly && isSelected
                  ? 'resize-handle resize-handle--right'
                  : '',
              bottom:
                !isReadOnly && isSelected
                  ? 'resize-handle resize-handle--bottom'
                  : '',
              left:
                !isReadOnly && isSelected
                  ? 'resize-handle resize-handle--left'
                  : '',
            }}
            enable={
              isReadOnly
                ? false
                : {
                    topRight: true,
                    topLeft: true,
                    bottomRight: true,
                    bottomLeft: true,
                    top: true,
                    right: true,
                    bottom: true,
                    left: true,
                  }
            }
          >
            <>
              {isLoading && (
                <div className="absolute h-full w-full bg-zinc-400/60 z-10">
                  <Spinner />
                </div>
              )}
              <Editor
                projectId={projectId}
                documentId={documentId}
                documentPageId={documentPageId}
                className={cn('flex-1 bg-transparent overflow-hidden')}
                onFocus={() => {
                  setIsEditorFocused(true)
                }}
                onBlur={({ editor }: { editor: TipTapEditor }) => {
                  setIsEditorFocused(false)
                  let updatedSize = containerDimensions
                  if (editor) {
                    updatedSize = adjustEditorHeight({
                      editor,
                      size: containerDimensions,
                    })
                  }
                  adjustEditorHeight({
                    editor,
                    size: updatedSize,
                  })
                  saveNotes({ editor })
                }}
                onPointerDown={(e) => {
                  multiSelectKeydownHandler(e as any, template)
                }}
                onPointerUp={(e) => {
                  multiSelectKeyupHandler(e)
                }}
                onClick={() => {
                  setSelectedElement({
                    id: template?.id as string,
                    documentPageId,
                    isRemovable: false,
                    type: dataType,
                  })
                }}
                onUpdate={({ editor }: { editor: TipTapEditor }) => {
                  let updatedSize = containerDimensions
                  if (editor) {
                    updatedSize = adjustEditorHeight({
                      editor,
                      size: containerDimensions,
                    })
                  }
                  adjustEditorHeight({
                    editor,
                    size: updatedSize,
                  })
                  saveNotes({ editor })
                }}
                content={content}
                disabled={Boolean(!isEditorFocused && isReadOnly)}
                isMultiSelecting={Boolean(
                  !isEditorFocused && isSelectKeyPressed,
                )}
                placeholder="Write something or press '/' for commands..."
                isDragging={isDragging}
                updateBomWithSlashCommands={true}
                isTransparent={true}
                editorRef={editorRef}
                isTextSelectionEnabled={isSelected}
              />
            </>
          </Resizable>
        </div>
      </div>
    </OptionsContextMenu>
  )
}
